import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { AREA_PAGES } from '../areaPages'
import { getCoverage, getFindings, getOverview, getOverviewTrend, getReportSummary, referenceOf } from '../../lib/api'
import type { AssessmentChanges, Coverage, FindingSummary, Overview, OverviewTrend } from '../../lib/api'
import { CoverageLedger } from './CoverageLedger'
import { canRescan, Freshness, RescanButton } from './Freshness'
import { FindingScope } from './FindingScope'
import { formatDate } from './LiveFindings'
import { FindingTitle, NotLicensedCard, severityTag, UnreadControls, useScanChanges } from './LiveDashboard'
import { notAssessedAreasSentence, notLicensedOf, wholeAreaNotLicensed } from './notLicensed'
import { partlyReadAreas, partlyReadNote, unconfirmedAreas } from './partlyRead'
import { agrees, personOf, SESSION_CHANGED, SESSION_UNKNOWN, useSession } from './session'
import { NO_DEVICES_FINDING } from './unreadReasons'
import { describeFailure, useApi } from './useApi'
import type { Load } from './useApi'
import './live.css'

/**
 * The redesigned dashboard (owner, 2026-10-11: "go with the recommendations" on the dashboard proposal).
 *
 * Built around six questions, in this order: is the picture complete (the status line), how exposed are
 * we (five headline numbers), what to fix first, where (each area split by severity), and what was not
 * checked (behind Details, never gone). Slice 2 adds better-or-worse: the findings past their target
 * (owner, 2026-10-11: Critical 14 days, High 30, Medium 60, Low 90), thirty days of open findings, what
 * the last assessment changed, Microsoft's score over the month, and set-aside reviews falling due.
 *
 * Shown only to workspaces on the preview list (Me.dashboardPreview); everyone else keeps the current
 * dashboard. Every rule of the current one stands: nothing unchecked is drawn as clean, a count comes
 * from the server's single snapshot, and a number that could not be read says so instead of 0.
 */

const RANKED = ['Critical', 'High', 'Medium', 'Low'] as const
const FIX_FIRST_ROWS = 6
const DEFENDER_URL = 'https://security.microsoft.com/securescore'

/** Days between a timestamp and now, whole, never negative. */
export function daysSince(iso: string, now = Date.now()): number {
  const then = Date.parse(iso)
  return Number.isNaN(then) ? 0 : Math.max(0, Math.floor((now - then) / 86_400_000))
}

/** "today", "1 day ago", "34 days ago". */
export function seenAgo(iso: string, now = Date.now()): string {
  const n = daysSince(iso, now)
  return n === 0 ? 'first seen today' : n === 1 ? 'first seen 1 day ago' : `first seen ${n} days ago`
}

/** Whether a finding has been open longer than its severity's target, from first seen. */
export function pastTarget(f: FindingSummary, targets: Record<string, number> | undefined, now = Date.now()): number | null {
  const days = targets?.[f.severity]
  return days != null && daysSince(f.firstSeenAt, now) > days ? days : null
}

/**
 * "Fix these first": Critical then High, oldest first within a severity, so what is being neglected rises
 * rather than what was seen most recently (owner decision 4). Medium and Low fill in only when there are
 * fewer than six urgent findings. Set-aside findings never reach here: the list is active findings only.
 */
export function fixFirst(items: FindingSummary[], limit = FIX_FIRST_ROWS): FindingSummary[] {
  const rank = (s: string) => {
    const i = RANKED.indexOf(s as (typeof RANKED)[number])
    return i === -1 ? RANKED.length : i
  }
  return [...items]
    .filter((f) => f.status === 'active')
    .sort((a, b) => rank(a.severity) - rank(b.severity) || Date.parse(a.firstSeenAt) - Date.parse(b.firstSeenAt))
    .slice(0, limit)
}

export function DashboardV2({ onOpen }: { onOpen?: (finding: FindingSummary) => void }) {
  const session = useSession()
  const [queued, setQueued] = useState(0)
  const running = (data: { freshness: Overview['freshness'] }) =>
    data.freshness.status === 'running' || data.freshness.scanPending

  const overview = useApi(() => getOverview(), [queued], running, true)
  const worst = useApi(() => getFindings({ status: 'active', sort: 'severity', pageSize: 100 }), [queued], running, true)
  const scanChanges = useScanChanges(overview.state === 'ready' ? overview.data.freshness : null)
  const coverage = useApi(() => getCoverage(), [])
  // Slice 2: the month's direction, and what the last assessment changed (the same read the report's
  // executive page uses, so the two always agree). Both supplementary: the page stands without them.
  const trend = useApi(() => getOverviewTrend(), [queued, scanChanges], undefined, true)
  const summary = useApi(() => getReportSummary(), [queued, scanChanges], undefined, true)

  const headerStatus = useRef<HTMLParagraphElement>(null)
  const scanMoving = overview.state === 'ready' && !canRescan(overview.data.freshness)
  useEffect(() => {
    if (queued > 0 && scanMoving) headerStatus.current?.focus()
  }, [queued, scanMoving])

  if (overview.state === 'loading') return <div className="live-empty">Loading…</div>
  if (overview.state === 'error') return <div className="live-error">{describeFailure(overview.status)}</div>
  if (!agrees(session, overview.data.workspaceId)) {
    return <div className="live-error">{session.state === 'unknown' ? SESSION_UNKNOWN : SESSION_CHANGED}</div>
  }

  const o = overview.data
  const { freshness, activeBySeverity } = o
  const me = personOf(session)
  const items = worst.state === 'ready' ? worst.data.items : []
  const total = Object.values(activeBySeverity).reduce((sum, n) => sum + n, 0)
  const neverAssessed = freshness.status === null
  const notLicensed = notLicensedOf(freshness)
  const gaps = o.unreadControls.length + notLicensed.reduce((n, g) => n + g.controls, 0)
  // An answer for another workspace is never drawn under this one's name: it reads as unavailable.
  const trendData = trend.state === 'ready' && agrees(session, trend.data.workspaceId) ? trend.data : null
  const changes: AssessmentChanges | null | 'unavailable' | 'loading' =
    summary.state === 'loading'
      ? 'loading'
      : summary.state === 'ready' && agrees(session, summary.data.workspaceId)
        ? summary.data.changes
        : 'unavailable'

  return (
    <div className="dv2">
      <header className="dash-header">
        <div className="dash-header-who">
          <h2 className="dash-title">{me?.workspaceName ?? 'Dashboard'}</h2>
        </div>
        <div className="dash-header-action">
          {canRescan(freshness) ? (
            <RescanButton onQueued={() => setQueued((n) => n + 1)} label="Assess now" />
          ) : (
            <p ref={headerStatus} tabIndex={-1} className="dash-header-status" role="status">
              {freshness.status === 'running'
                ? 'Assessment running — this page updates as each area finishes.'
                : 'Assessment queued — it starts as soon as the scanner is free, and this page updates when it does.'}
            </p>
          )}
        </div>
      </header>

      {/* A · is the picture complete. The same line as today; the long gap cards move behind Details. */}
      <Freshness freshness={freshness} showRescan={false} />
      {gaps > 0 ? (
        <details className="dv2-gaps">
          <summary>
            Details: {gaps} {gaps === 1 ? 'control was' : 'controls were'} not assessed on the most recent assessment
          </summary>
          <UnreadControls
            controls={o.unreadControls}
            noDevicesFinding={items.find((f) => f.catalogKey === NO_DEVICES_FINDING.key)?.title ?? NO_DEVICES_FINDING.title}
          />
          <NotLicensedCard groups={notLicensed} />
        </details>
      ) : null}

      {/* B · how exposed are we */}
      <HeadlineNumbers
        overview={o}
        total={total}
        neverAssessed={neverAssessed}
        coverage={coverage}
        changes={typeof changes === 'object' ? changes : null}
        scores={trendData?.secureScore ?? []}
      />

      {/* C · what to fix first, D · where */}
      <div className="dv2-main">
        <FixFirst
          rows={fixFirst(items)}
          load={worst.state}
          loadStatus={worst.state === 'error' ? worst.status : 0}
          total={total}
          suppressed={o.suppressed}
          trustworthy={freshness.complete}
          unowned={o.unownedCriticalHigh}
          urgentTotal={(activeBySeverity.Critical ?? 0) + (activeBySeverity.High ?? 0)}
          urgentLoaded={items.filter((f) => f.severity === 'Critical' || f.severity === 'High').length}
          notAssessed={notAssessedAreasSentence(freshness)}
          targets={o.remediationTargetDays}
          onOpen={onOpen}
        />
        <ByArea overview={o} />
      </div>

      {/* E · better or worse */}
      <div className="dv2-main">
        <Trend trend={trend.state === 'loading' ? 'loading' : trendData ?? 'unavailable'} />
        <Changes changes={changes} />
      </div>

      {/* F · coverage, Microsoft's score, what is closed */}
      <div className="dv2-foot">
        <section className="dv2-card" aria-labelledby="dv2-coverage">
          <h3 id="dv2-coverage" className="dv2-card-title">SCuBA Entra ID coverage</h3>
          {coverage.state === 'ready' ? (
            <>
              <p className="dv2-big">{coverage.data.withCheck} <span>of {coverage.data.total}</span></p>
              <p className="dv2-note">
                CISA SCuBA Entra ID policies Defenssive can check. {coverage.data.byStatus.assessed ?? 0} are
                checked every time; the rest depend on your licences or cannot be read by apps.
              </p>
            </>
          ) : (
            <p className="dv2-note">{coverage.state === 'error' ? describeFailure(coverage.status) : 'Loading…'}</p>
          )}
        </section>
        <section className="dv2-card" aria-labelledby="dv2-score">
          <h3 id="dv2-score" className="dv2-card-title">Microsoft Secure Score</h3>
          <SecureScoreSummary overview={o} scores={trendData?.secureScore ?? []} />
        </section>
        <SetAside suppressed={o.suppressed} reviewsDue={o.setAsideReviewsDue} />
      </div>

      <details className="dv2-coverage-details">
        <summary>Coverage details: what Defenssive checks, and what it cannot</summary>
        <CoverageLedger />
      </details>
    </div>
  )
}

function HeadlineNumbers({
  overview: o,
  total,
  neverAssessed,
  coverage,
  changes,
  scores,
}: {
  overview: Overview
  total: number
  neverAssessed: boolean
  coverage: Load<Coverage>
  changes: AssessmentChanges | null
  scores: OverviewTrend['secureScore']
}) {
  const critical = o.activeBySeverity.Critical ?? 0
  const urgent = critical + (o.activeBySeverity.High ?? 0)
  const areas = Object.keys(o.activeByArea).length
  const score = o.secureScore
  const scorePct =
    score && score.status === 'measured' && score.currentScore != null && score.maxScore
      ? Math.round((score.currentScore / score.maxScore) * 100)
      : null
  const targets = o.remediationTargetDays
  const overdue = o.overdueBySeverity ? Object.values(o.overdueBySeverity).reduce((n, v) => n + v, 0) : null
  // What the last assessment changed, once there is an earlier one to compare with.
  const arrived = changes?.previous ? changes.new.count + changes.reopened.count : null

  return (
    <dl className="dv2-kpis" aria-label="Headline numbers">
      <div className="dv2-kpi">
        <dt>Open findings</dt>
        <dd className="dv2-kpi-value">{neverAssessed ? '—' : total}</dd>
        <dd className="dv2-kpi-sub">
          {neverAssessed
            ? 'No assessment yet'
            : arrived !== null && changes
              ? `${arrived} new, ${changes.resolved.count} resolved since the last assessment`
              : `across ${areas} ${areas === 1 ? 'area' : 'areas'}`}
        </dd>
      </div>
      <div className="dv2-kpi">
        <dt>Critical and high</dt>
        <dd className={urgent > 0 ? 'dv2-kpi-value dv2-urgent' : 'dv2-kpi-value'}>{neverAssessed ? '—' : urgent}</dd>
        <dd className="dv2-kpi-sub">{neverAssessed ? 'No assessment yet' : `${critical} critical`}</dd>
      </div>
      <div className="dv2-kpi">
        <dt>Past target</dt>
        <dd className={overdue ? 'dv2-kpi-value dv2-urgent' : 'dv2-kpi-value'}>
          {neverAssessed || overdue === null ? '—' : overdue}
        </dd>
        <dd className="dv2-kpi-sub">
          {neverAssessed
            ? 'No assessment yet'
            : targets
              ? `Critical ${targets.Critical} days, high ${targets.High}, medium ${targets.Medium}, low ${targets.Low}`
              : 'Not counted yet'}
        </dd>
      </div>
      <div className="dv2-kpi">
        <dt>Oldest critical or high</dt>
        {o.oldestCriticalHighFirstSeen ? (
          <>
            <dd className="dv2-kpi-value">
              {daysSince(o.oldestCriticalHighFirstSeen)}{' '}
              <small>{daysSince(o.oldestCriticalHighFirstSeen) === 1 ? 'day' : 'days'}</small>
            </dd>
            <dd className="dv2-kpi-sub">since first seen</dd>
          </>
        ) : (
          <>
            <dd className="dv2-kpi-value">—</dd>
            <dd className="dv2-kpi-sub">{neverAssessed ? 'No assessment yet' : 'None open'}</dd>
          </>
        )}
      </div>
      <div className="dv2-kpi">
        <dt>SCuBA Entra ID coverage</dt>
        <dd className="dv2-kpi-value">
          {coverage.state === 'ready' ? (
            <>
              {coverage.data.withCheck} <small>of {coverage.data.total}</small>
            </>
          ) : (
            '…'
          )}
        </dd>
        <dd className="dv2-kpi-sub">Policies we can check</dd>
      </div>
      <div className="dv2-kpi">
        <dt>Microsoft Secure Score</dt>
        <dd className="dv2-kpi-value">
          {scorePct === null ? <small>Not read</small> : <>{scorePct}<small>%</small></>}
          {scorePct !== null && scores.length >= 2 ? <ScoreSparkline scores={scores} /> : null}
        </dd>
        <dd className="dv2-kpi-sub">Microsoft&rsquo;s own number</dd>
      </div>
    </dl>
  )
}

function FixFirst({
  rows,
  load,
  loadStatus,
  total,
  suppressed,
  trustworthy,
  unowned,
  urgentTotal,
  urgentLoaded,
  notAssessed,
  targets,
  onOpen,
}: {
  rows: FindingSummary[]
  load: 'loading' | 'ready' | 'error'
  loadStatus: number
  total: number
  suppressed: number
  trustworthy: boolean
  unowned: number | undefined
  urgentTotal: number
  urgentLoaded: number
  notAssessed: string
  targets: Record<string, number> | undefined
  onOpen?: (finding: FindingSummary) => void
}) {
  return (
    <section className="dv2-card dv2-fix" aria-labelledby="dv2-fix">
      <div className="dv2-card-head">
        <h3 id="dv2-fix" className="dv2-card-title">Fix these first</h3>
        {unowned != null && unowned > 0 ? (
          <span className="dv2-card-note">
            {unowned} critical or high {unowned === 1 ? 'finding has' : 'findings have'} no owner
          </span>
        ) : null}
      </div>

      {/* The all-clear is decided from the overview's own counts, never from this separate list. */}
      {load === 'error' ? (
        <p className="live-error">{describeFailure(loadStatus)}</p>
      ) : total === 0 ? (
        <p className="live-empty">
          {!trustworthy
            ? 'Nothing to rank yet — the assessment above has not produced a complete set of findings.'
            : suppressed > 0
              ? `No active findings — ${suppressed} set aside. Open the Set-aside list to be sure none still needs action; this is not an all-clear.`
              : notAssessed
                ? `No open findings in the areas that were assessed. ${notAssessed}`
                : 'No open findings. Nothing needs fixing from this assessment.'}
        </p>
      ) : rows.length === 0 ? (
        <p className="live-empty">Loading…</p>
      ) : (
        <ol className="dv2-fix-list">
          {rows.map((f) => (
            <li key={f.id}>
              <div className="dv2-fix-title">
                <FindingTitle finding={f} onOpen={onOpen} />
                <FindingScope summary={f.summary} />
              </div>
              <div className="dv2-fix-sev">{severityTag(f.severity)}</div>
              <div className="dv2-fix-meta">
                {[f.area, seenAgo(f.firstSeenAt), f.owner ?? 'No owner']
                  .filter(Boolean)
                  .join(' · ')}
                {pastTarget(f, targets) !== null ? (
                  <span className="dv2-overdue"> · past the {pastTarget(f, targets)}-day target</span>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      )}

      {urgentTotal > urgentLoaded && rows.length > 0 ? (
        <p className="dv2-note">Ordered among the {urgentLoaded} most severe findings loaded here.</p>
      ) : null}
      {total > 0 ? (
        <Link className="dv2-more" to="/recommendations">
          See all {total} in Recommendations
        </Link>
      ) : null}
    </section>
  )
}

interface AreaRowV2 {
  area: string
  total: number
  split: Record<string, number>
  note: string | null
  link: string | undefined
}

function ByArea({ overview: o }: { overview: Overview }) {
  const session = useSession()
  const me = personOf(session)
  const [focus, setFocus] = useState<string | null>(null)
  const { freshness } = o

  // The same rows today's dashboard draws: every area the findings or the scan know about, narrowed to
  // what this build assesses, keeping any area that still has open findings.
  const current = o.assessedAreas ? new Set(o.assessedAreas) : null
  const names = [...new Set([...Object.keys(o.activeByArea), ...freshness.areas.map((a) => a.area)])]
    .filter((area) => current === null || current.has(area) || (o.activeByArea[area] ?? 0) > 0)

  const partly = new Map(partlyReadAreas(freshness, { finishedOnly: true }).map((p) => [p.area, p]))
  const unconfirmed = new Set(unconfirmedAreas(freshness))

  const rows: AreaRowV2[] = names.map((area) => {
    const total = o.activeByArea[area] ?? 0
    const split = (o.activeByAreaAndSeverity?.[area] ?? {}) as Record<string, number>
    const status = freshness.areas.find((a) => a.area === area)?.status
    const p = partly.get(area)
    const note = wholeAreaNotLicensed(freshness, area)
      ? 'Not assessed: your tenant has no licence for the service it reads.'
      : status && status !== 'complete' && total === 0
        ? 'Not complete on the last assessment, so this is not a clean result.'
        : status && status !== 'complete'
          ? 'Not complete on the last assessment: findings may be missing.'
          : p
            ? partlyReadNote(p)
            : unconfirmed.has(area)
              ? 'Could not confirm that every check here read its evidence in full.'
              : null
    const page = (area === 'Cloud' || area === 'AI') && !current?.has(area) ? undefined : AREA_PAGES[area]
    return { area, total, split, note, link: page }
  })
  rows.sort((a, b) => b.total - a.total || a.area.localeCompare(b.area))
  const max = Math.max(1, ...rows.map((r) => r.total))

  // Off Monitor, Cloud and AI are part of Monitor: said as that, never drawn as a bar or a zero.
  const monitorOnly = me?.monitoringActive === true
    ? []
    : [me?.assessesAzure ? 'Cloud' : null, me?.assessesAiPosture ? 'AI' : null].filter((x): x is string => x !== null)

  const describe = (r: AreaRowV2) =>
    `${r.area}: ${r.total} open` +
    (Object.keys(r.split).length > 0
      ? `, ${RANKED.map((s) => `${r.split[s] ?? 0} ${s.toLowerCase()}`).join(', ')}`
      : '')
  const focused = rows.find((r) => r.area === focus)

  return (
    <section className="dv2-card" aria-labelledby="dv2-areas">
      <div className="dv2-card-head">
        <h3 id="dv2-areas" className="dv2-card-title">By area</h3>
        <span className="dv2-card-note">Open findings, split by severity</span>
      </div>
      <ul className="dv2-areas">
        {rows.map((r) => {
          const bar = (
            <>
              <span className="dv2-area-name">{r.area}</span>
              <span className="dv2-track" aria-hidden="true">
                <span className="dv2-bar" style={{ width: `${(r.total / max) * 100}%` }}>
                  {Object.keys(r.split).length > 0 ? (
                    RANKED.map((s) =>
                      r.split[s] ? (
                        <i key={s} className={`dv2-seg dv2-seg-${s.toLowerCase()}`} style={{ width: `${(r.split[s] / r.total) * 100}%` }} />
                      ) : null,
                    )
                  ) : (
                    <i className="dv2-seg dv2-seg-unsplit" style={{ width: '100%' }} />
                  )}
                </span>
              </span>
              <span className="dv2-area-count">{r.total}</span>
            </>
          )
          return (
            <li key={r.area}>
              {r.link ? (
                <Link
                  className="dv2-area-row"
                  to={r.link}
                  aria-label={describe(r)}
                  onMouseEnter={() => setFocus(r.area)}
                  onFocus={() => setFocus(r.area)}
                >
                  {bar}
                </Link>
              ) : (
                <div className="dv2-area-row" aria-label={describe(r)} role="group">{bar}</div>
              )}
              {r.note ? <p className="dv2-area-note">{r.note}</p> : null}
            </li>
          )
        })}
        {monitorOnly.map((area) => (
          <li key={area}>
            <div className="dv2-area-row dv2-area-locked">
              <span className="dv2-area-name">{area}</span>
              <span className="dv2-area-note-inline">
                Part of the Monitor plan. <Link to="/plans">See plans</Link>
              </span>
            </div>
          </li>
        ))}
      </ul>
      <div className="dv2-legend" aria-hidden="true">
        {RANKED.map((s) => (
          <span key={s}><i className={`dv2-swatch dv2-seg-${s.toLowerCase()}`} />{s}</span>
        ))}
      </div>
      <p className="dv2-area-tip" aria-live="polite">
        {focused ? `${describe(focused)}. Select to open ${focused.area}.` : 'Point to an area to see its split.'}
      </p>
    </section>
  )
}

function SecureScoreSummary({ overview: o, scores }: { overview: Overview; scores: OverviewTrend['secureScore'] }) {
  const r = o.secureScore
  const first = scores[0]
  const last = scores[scores.length - 1]
  const moved = first && last && scores.length >= 2 ? Math.round(last.currentScore - first.currentScore) : null
  if (!r) return <p className="dv2-note">Not read yet: it is read with each assessment.</p>
  if (r.status !== 'measured' || r.currentScore == null || r.maxScore == null) {
    return <p className="dv2-note">Microsoft did not return a score on the last assessment, so none is shown.</p>
  }
  return (
    <>
      <p className="dv2-big">
        {Math.round(r.currentScore)} <span>of {Math.round(r.maxScore)} points</span>
      </p>
      <p className="dv2-note">
        {moved !== null ? (
          <>
            {moved > 0
              ? `Up ${moved} ${moved === 1 ? 'point' : 'points'} since ${dayLabel(first!.day)}. `
              : moved < 0
                ? `Down ${-moved} ${moved === -1 ? 'point' : 'points'} since ${dayLabel(first!.day)}. `
                : `No change since ${dayLabel(first!.day)}. `}
          </>
        ) : null}
        Microsoft&rsquo;s own rating, not a Defenssive score.{' '}
        <a href={DEFENDER_URL} target="_blank" rel="noopener noreferrer">Open in Microsoft Defender</a>
      </p>
    </>
  )
}

/** "Oct 11" for a UTC day ("2026-10-11"), in the reader's own locale. */
export function dayLabel(day: string): string {
  const d = new Date(`${day}T00:00:00Z`)
  return Number.isNaN(d.getTime()) ? day : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

/** The last 30 UTC days, oldest first, as "YYYY-MM-DD". */
export function lastThirtyDays(now = Date.now()): string[] {
  const today = Date.UTC(new Date(now).getUTCFullYear(), new Date(now).getUTCMonth(), new Date(now).getUTCDate())
  return Array.from({ length: 30 }, (_, i) => new Date(today - (29 - i) * 86_400_000).toISOString().slice(0, 10))
}

/**
 * E · thirty days of open findings, with critical and high drawn under them. One point per day that had
 * a finished assessment; a day without one is a gap, never a line drawn across days nothing was looked
 * at. Named in words for a screen reader, first point to last.
 */
function Trend({ trend }: { trend: OverviewTrend | 'loading' | 'unavailable' }) {
  const body = () => {
    if (trend === 'loading') return <p className="dv2-note">Loading…</p>
    if (trend === 'unavailable') return <p className="dv2-note">The trend could not be loaded just now.</p>
    if (trend.points.length < 2) {
      return <p className="dv2-note">The trend appears after your next assessment.</p>
    }

    const days = lastThirtyDays()
    const byDay = new Map(trend.points.map((p) => [p.day, p]))
    const top = Math.max(1, ...trend.points.map((p) => p.open))
    const W = 480
    const H = 190
    const PAD = { l: 34, r: 44, t: 10, b: 22 }
    const x = (i: number) => PAD.l + (i / (days.length - 1)) * (W - PAD.l - PAD.r)
    const y = (v: number) => PAD.t + (1 - v / top) * (H - PAD.t - PAD.b)

    // Runs of consecutive days with a point; a missing day ends a run.
    const runs = (pick: (p: OverviewTrend['points'][number]) => number) => {
      const out: { i: number; v: number }[][] = []
      let run: { i: number; v: number }[] = []
      days.forEach((day, i) => {
        const p = byDay.get(day)
        if (p) run.push({ i, v: pick(p) })
        else if (run.length) { out.push(run); run = [] }
      })
      if (run.length) out.push(run)
      return out
    }
    const draw = (series: { i: number; v: number }[][], cls: string) =>
      series.map((run, k) =>
        run.length === 1 ? (
          <circle key={k} className={cls} cx={x(run[0]!.i)} cy={y(run[0]!.v)} r={2.5} />
        ) : (
          <polyline key={k} className={cls} fill="none" points={run.map((p) => `${x(p.i)},${y(p.v)}`).join(' ')} />
        ),
      )

    const first = trend.points[0]!
    const last = trend.points[trend.points.length - 1]!
    const lastIndex = days.indexOf(last.day)
    const label =
      `Open findings went from ${first.open} on ${dayLabel(first.day)} to ${last.open} on ${dayLabel(last.day)}; ` +
      `critical and high from ${first.criticalHigh} to ${last.criticalHigh}. ` +
      `${trend.points.length} of the last 30 days had an assessment.`

    return (
      <>
        <svg className="dv2-trend" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
          {[0, 0.5, 1].map((f) => (
            <g key={f}>
              <line className="dv2-trend-grid" x1={PAD.l} x2={W - PAD.r} y1={y(top * f)} y2={y(top * f)} />
              <text className="dv2-trend-axis" x={PAD.l - 6} y={y(top * f) + 4} textAnchor="end">{Math.round(top * f)}</text>
            </g>
          ))}
          {draw(runs((p) => p.open), 'dv2-trend-open')}
          {draw(runs((p) => p.criticalHigh), 'dv2-trend-urgent')}
          {lastIndex >= 0 ? (
            <>
              <circle className="dv2-trend-open-dot" cx={x(lastIndex)} cy={y(last.open)} r={3.5} />
              <text className="dv2-trend-end" x={x(lastIndex) + 7} y={y(last.open) + 4}>{last.open}</text>
              <text className="dv2-trend-end dv2-trend-end-urgent" x={x(lastIndex) + 7} y={y(last.criticalHigh) + 4}>{last.criticalHigh}</text>
            </>
          ) : null}
          <text className="dv2-trend-axis" x={PAD.l} y={H - 6}>{dayLabel(days[0]!)}</text>
          <text className="dv2-trend-axis" x={W - PAD.r} y={H - 6} textAnchor="end">{dayLabel(days[days.length - 1]!)}</text>
        </svg>
        <div className="dv2-legend" aria-hidden="true">
          <span><i className="dv2-swatch dv2-trend-key-open" />All open</span>
          <span><i className="dv2-swatch dv2-seg-high" />Critical and high</span>
        </div>
      </>
    )
  }

  return (
    <section className="dv2-card" aria-labelledby="dv2-trend">
      <div className="dv2-card-head">
        <h3 id="dv2-trend" className="dv2-card-title">Open findings, last 30 days</h3>
        <span className="dv2-card-note">At the last assessment of each day</span>
      </div>
      {body()}
    </section>
  )
}

/**
 * What the last assessment changed against the one before it: the report's executive page's own read,
 * so the dashboard and the report never disagree. The names are one click away.
 */
function Changes({ changes }: { changes: AssessmentChanges | null | 'unavailable' | 'loading' }) {
  const body = () => {
    if (changes === 'loading') return <p className="dv2-note">Loading…</p>
    if (changes === 'unavailable') return <p className="dv2-note">The changes could not be loaded just now.</p>
    if (changes === null || changes.previous === null) {
      return <p className="dv2-note">Changes appear after your next assessment.</p>
    }

    const groups = [
      { label: 'New', list: changes.new },
      { label: 'Resolved', list: changes.resolved },
      { label: 'Came back', list: changes.reopened },
    ]
    const named = groups.filter((g) => g.list.items.length > 0)
    return (
      <>
        <p className="dv2-card-note">
          Compared with the assessment of {formatDate(changes.previous.completedAt)}
        </p>
        <dl className="dv2-changes">
          {groups.map((g) => (
            <div key={g.label}>
              <dt>{g.label}</dt>
              <dd>{g.list.count}</dd>
            </div>
          ))}
        </dl>
        {!changes.assessment.mayResolve ? (
          <p className="dv2-note">
            Only a check that read its evidence in full can mark a finding resolved, so a fix in a part this
            assessment did not fully read shows as resolved after an assessment that reads it.
          </p>
        ) : null}
        {named.length > 0 ? (
          <details className="dv2-changes-names">
            <summary>See which findings</summary>
            {named.map((g) => (
              <div key={g.label}>
                <p className="dv2-changes-group">{g.label}</p>
                <ul>
                  {g.list.items.map((item) => (
                    <li key={item.id}>
                      {severityTag(item.severity)} <Link to={`/findings/${referenceOf(item.id)}`}>{item.title}</Link>
                    </li>
                  ))}
                </ul>
                {g.list.count > g.list.items.length ? (
                  <p className="dv2-note">and {g.list.count - g.list.items.length} more</p>
                ) : null}
              </div>
            ))}
          </details>
        ) : null}
      </>
    )
  }

  return (
    <section className="dv2-card" aria-labelledby="dv2-changes">
      <h3 id="dv2-changes" className="dv2-card-title">Since the last assessment</h3>
      {body()}
    </section>
  )
}

/** Microsoft's score over the month, as a mark beside the number: no axis, the reading is the number. */
function ScoreSparkline({ scores }: { scores: OverviewTrend['secureScore'] }) {
  const pct = scores.map((s) => (s.maxScore > 0 ? (s.currentScore / s.maxScore) * 100 : 0))
  const lo = Math.min(...pct)
  const hi = Math.max(...pct)
  const span = Math.max(1, hi - lo)
  const W = 64
  const H = 18
  const pts = pct.map((v, i) => `${(i / (pct.length - 1)) * W},${H - 2 - ((v - lo) / span) * (H - 4)}`).join(' ')
  return (
    <svg className="dv2-spark" viewBox={`0 0 ${W} ${H}`} width={W} height={H} aria-hidden="true">
      <polyline fill="none" points={pts} />
    </svg>
  )
}

/**
 * F · set aside: what has been accepted or suppressed, and how many of those decisions come up for
 * review within 30 days. The list itself is the Recommendations page's Set-aside view.
 */
function SetAside({ suppressed, reviewsDue }: { suppressed: number; reviewsDue: number | undefined }) {
  return (
    <section className="dv2-card" aria-labelledby="dv2-setaside">
      <h3 id="dv2-setaside" className="dv2-card-title">Set aside</h3>
      <p className="dv2-big">
        {suppressed} <span>{suppressed === 1 ? 'finding' : 'findings'}</span>
      </p>
      <p className="dv2-note">
        {suppressed === 0
          ? 'Nothing is set aside.'
          : reviewsDue == null
            ? 'Accepted risks and suppressed findings, each with its reason on record.'
            : reviewsDue === 0
              ? 'No review falls due in the next 30 days.'
              : `${reviewsDue} ${reviewsDue === 1 ? 'review falls' : 'reviews fall'} due in the next 30 days.`}
      </p>
      {suppressed > 0 ? (
        <Link className="dv2-more" to="/recommendations?view=set-aside">Open the Set-aside list</Link>
      ) : null}
    </section>
  )
}
